import { Alert, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Fragment, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import z from 'zod';
import { getActivity } from '../../../api/activities';
import { getChangeRequestValidationRules, getIs37, getIs37ValidationRules } from '../../../api/documents/is37s';
import { DocumentType } from '../../../api/dto';
import { ImpactHelmet } from '../../../components/ImpactHelmet';
import { defaultUrlParamsSchema, withUrlParams } from '../../../components/wrappers/withUrlParams';
import { Is37Form } from '../../../forms/documents/Is37Form';
import { IS37PageVariant } from './IS37PageVariant';

const schema = z.union([
    defaultUrlParamsSchema.extend({
        variant: z.literal(IS37PageVariant.CREATE),
        queryParams: z.object({
            activity: z.coerce.number(),
        }),
    }),
    defaultUrlParamsSchema.extend({
        variant: z.union([z.literal(IS37PageVariant.CLONE), z.literal(IS37PageVariant.READ)]),
        pathParams: z.object({
            id: z.coerce.number(),
        }),
    }),
]);

const IS37PageContent: React.FC<z.infer<typeof schema>> = ({ variant, pathParams, queryParams }) => {
    const intl = useIntl();

    const {
        data: is37Data,
        isLoading: is37DataLoading,
        isError: is37DataFailed,
        remove,
        refetch,
    } = useQuery(
        [DocumentType.IS37, variant !== IS37PageVariant.CREATE ? pathParams.id : undefined],
        () => {
            if (variant === IS37PageVariant.CREATE) {
                throw new Error();
            }
            return getIs37({ id: String(pathParams.id) });
        },
        {
            enabled: variant !== IS37PageVariant.CREATE,
        }
    );

    const activityId = variant !== IS37PageVariant.CREATE ? is37Data?.activity.id : queryParams.activity;

    const {
        data: activityData,
        isLoading: isActivityDataLoading,
        isError: activityDataFailed,
    } = useQuery(['activity', activityId], () => getActivity({ id: String(activityId) }), {
        enabled: activityId !== undefined,
    });

    const {
        data: rulesData,
        isLoading: isRulesDataLoading,
        isError: rulesDataFailed,
    } = useQuery([DocumentType.IS37, variant === IS37PageVariant.READ ? pathParams.id : undefined, 'rules'], () =>
        getIs37ValidationRules({ id: variant === IS37PageVariant.READ ? pathParams.id : undefined })
    );

    const {
        data: changeRequestRulesData,
        isLoading: isChangeRequestRulesDataLoading,
        isError: changeRequestRulesDataFailed,
    } = useQuery([DocumentType.IS37, variant === IS37PageVariant.READ ? pathParams.id : undefined, 'cr-rules'], () =>
        getChangeRequestValidationRules({ id: variant === IS37PageVariant.READ ? pathParams.id : undefined })
    );

    const refreshData = useCallback(() => {
        remove();
        return refetch();
    }, [remove, refetch]);

    if (
        (is37DataLoading && variant !== IS37PageVariant.CREATE) ||
        (isActivityDataLoading && activityId !== undefined) ||
        isRulesDataLoading ||
        isChangeRequestRulesDataLoading
    ) {
        return (
            <>
                <LinearProgress />
                <ImpactHelmet documentType={DocumentType.IS37} title={intl.formatMessage({ id: 'title.loading' })} />
            </>
        );
    } else if (is37DataFailed || activityDataFailed || rulesDataFailed || changeRequestRulesDataFailed) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <FormattedMessage id="is37.error.document" />
                <ImpactHelmet documentType={DocumentType.IS37} title={intl.formatMessage({ id: 'title.error' })} />
            </Alert>
        );
    } else if (
        (variant === IS37PageVariant.CREATE && activityData) ||
        ((variant === IS37PageVariant.READ || variant === IS37PageVariant.CLONE) &&
            is37Data &&
            activityData &&
            changeRequestRulesData)
    ) {
        return (
            <Is37Form
                data={
                    variant === IS37PageVariant.CREATE
                        ? { variant, activityData }
                        : { variant, is37Data: is37Data!, activityData }
                }
                rules={rulesData}
                changeRequestRules={changeRequestRulesData}
                refreshData={refreshData}
            />
        );
    } else {
        return <Fragment />;
    }
};

export const IS37Page = withUrlParams(IS37PageContent, schema);
